import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useRef, } from "react";
import { SubscriptionRegistry, } from "./SubscriptionRegistry.js";
/**
 * The registry the hooks below reach, or `undefined` outside a provider.
 *
 * Undefined rather than a stub, so `useLiveReload` can say so once rather than
 * silently doing nothing — a page that has quietly stopped updating looks
 * exactly like one that is up to date.
 */
const RealtimeContext = createContext(undefined);
/**
 * Owns the one registry for the application and keeps it pointed at the
 * current connection.
 */
export function RealtimeProvider({ transport, channelPrefix, eventName, debounceMs, onListenerError, children, }) {
    // The error reporter is read through a ref so that passing a new function
    // each render does not rebuild the registry and re-subscribe everything.
    const reportError = useRef(onListenerError);
    reportError.current = onListenerError;
    const registry = useMemo(() => new SubscriptionRegistry({
        eventName,
        debounceMs,
        onListenerError: (error) => reportError.current?.(error),
    }), [eventName, debounceMs]);
    // An empty prefix is no prefix. Normalised here so nothing downstream has to
    // decide whether the empty string names a namespace.
    const prefix = channelPrefix === undefined || channelPrefix === ""
        ? undefined
        : channelPrefix;
    useEffect(() => {
        registry.retarget(transport, prefix);
        return () => {
            registry.retarget(undefined, undefined);
        };
    }, [registry, transport, prefix]);
    return (_jsx(RealtimeContext.Provider, { value: registry, children: children }));
}
export const NOT_IN_REALTIME_CONTEXT = "No realtime context. Did you forget to wrap the application in a <RealtimeProvider />?";
/**
 * The registry, for a caller that needs it directly. Prefer `useLiveReload`.
 */
export function useRealtimeRegistry() {
    return useContext(RealtimeContext);
}
//# sourceMappingURL=RealtimeProvider.js.map